import type { MouseEvent } from 'react';
import { HandshakeIcon, StackIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import type { Row, Table } from '@/lib/tanstack-table';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useOverrideOption,
  type AssignmentLine,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { toast } from 'src/components/ui/toast';
import { isBuyDesignated } from '../utils/lineFilters';
import { isQuantityEditable } from '../utils/lineQuantity';
import { buildBoardContractPriceOverrideInput } from './boardContractPriceApply';
import { getCartLineTableMeta } from './cartLineTableMeta';
import { useLineBulkEditTargets } from './lineBulkEditContext';
import { contractNumberFromCostAgreementId } from '../utils/boardContractNumber';
import { applyBulkContractPriceOverrides } from '../utils/bulkContractPriceOverride';
import { formatUnitCost } from './unitCostFormat';
import {
  hasUnitCostPricingDropdown,
  showUnitCostContractMarker,
} from './unitCostCellVisibility';
import { UnitCostPricingDropdownContent } from './UnitCostPricingDropdownContent';
import { VendorSwitchSection } from './VendorSwitchSection';
import { canSwitchVendor } from '../utils/vendorCandidateRows';

function stopRowSelect(event: MouseEvent): void {
  event.stopPropagation();
}

// fallow-ignore-next-line complexity
export function UnitCostCell({
  row,
  table,
}: {
  row: Row<AssignmentLine>;
  table: Table<AssignmentLine>;
}) {
  const line = row.original;
  const meta = getCartLineTableMeta(
    table as Parameters<typeof getCartLineTableMeta>[0],
  );
  const gt = useGT();
  const editTargets = useLineBulkEditTargets();
  const override = useOverrideOption();

  if (!isBuyDesignated(line)) {
    return <span className="text-xs text-gray-300">—</span>;
  }

  const option = line.topOption;
  const unitCost = option?.unitCost ?? null;
  if (unitCost == null) {
    return <span className="text-xs text-gray-300">—</span>;
  }

  const buyAction =
    option?.action === 'buy' || option?.action === 'buy_and_transfer'
      ? option.action
      : null;
  const canPickContract = isQuantityEditable(line) && buyAction != null;

  const tiers = line.vendorPriceBreakTiers;
  const options = line.buyContractPriceOptions;
  const priceBreak = option?.priceBreak;
  const quantity = option?.buyQuantity;

  const switchable = canSwitchVendor(line);
  const hasDropdown =
    switchable ||
    hasUnitCostPricingDropdown({
      tiers,
      options,
      priceBreak,
      quantity,
    });
  const showBreaksMarker = tiers.length > 0;
  const showContractMarker = showUnitCostContractMarker({
    options,
    priceBreak,
    quantity,
  });

  const lineById = (suggestionId: string) =>
    table
      .getRowModel()
      .rows.find((r) => r.original.suggestionId === suggestionId)?.original;

  const selectContractPrice = (costAgreementId: string) => {
    if (!canPickContract || meta.supplierId == null) return;
    const supplierId = meta.supplierId;
    const targetIds = editTargets(line.suggestionId);
    if (targetIds.length <= 1) {
      const input = buildBoardContractPriceOverrideInput({
        line,
        supplierId,
        costAgreementId,
      });
      if (input == null) return;
      override.mutate(input, {
        onError: () => {
          toast.error(
            meta.gt('Could not apply that contract price. Try again.', {
              context: 'replenishment contract price picker error',
            }),
          );
        },
      });
      return;
    }

    const bulkLines: AssignmentLine[] = [];
    for (const suggestionId of targetIds) {
      const targetLine =
        suggestionId === line.suggestionId ? line : lineById(suggestionId);
      if (targetLine != null) bulkLines.push(targetLine);
    }
    const contractNumber = contractNumberFromCostAgreementId(
      line,
      costAgreementId,
    );
    if (contractNumber == null) return;
    void applyBulkContractPriceOverrides(
      meta.gt,
      bulkLines,
      targetIds.length,
      supplierId,
      contractNumber,
      (input) => override.mutateAsync(input),
    );
  };

  const amount = (
    <span className="text-xs tabular-nums font-medium text-gray-700">
      {formatUnitCost(unitCost)}
    </span>
  );

  const markers = (showBreaksMarker || showContractMarker) && (
    <span className="inline-flex items-center gap-0.5">
      {showBreaksMarker && (
        <StackIcon className="h-3 w-3 text-sky-700" weight="fill" aria-hidden />
      )}
      {showContractMarker && (
        <HandshakeIcon
          className="h-3 w-3 text-violet-700"
          weight="fill"
          aria-hidden
        />
      )}
    </span>
  );

  if (!hasDropdown) {
    return (
      <span className="inline-flex flex-row-reverse items-center gap-1 whitespace-nowrap">
        {amount}
        {markers}
      </span>
    );
  }

  const menuLabel = gt('Unit cost pricing details', {
    context:
      'replenishment board unit cost cell: opens dropdown with breaks and contract pricing',
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            '-mr-0.5 inline-flex max-w-full flex-row-reverse items-center gap-1 whitespace-nowrap rounded px-0.5',
            'text-left hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
          aria-label={menuLabel}
          title={menuLabel}
          onClick={stopRowSelect}
          onPointerDown={stopRowSelect}
        >
          {amount}
          {markers}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={switchable ? 'w-[36rem] max-w-[calc(100vw-2rem)]' : 'w-72'}
        onClick={stopRowSelect}
        onPointerDown={stopRowSelect}
      >
        <UnitCostPricingDropdownContent
          tiers={tiers}
          options={options}
          priceBreak={priceBreak}
          quantity={quantity}
          disabled={!canPickContract || override.isPending}
          onSelectContract={selectContractPrice}
        />
        {switchable && (
          <VendorSwitchSection
            line={line}
            currentSupplierId={meta.supplierId}
            lineById={lineById}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
